// Mock data for dashboard metrics and charts
export interface DashboardMetrics {
    totalStates: number;
    totalDistricts: number;
    totalBlocks: number;
    totalGPs: number;
    totalVillages: number;
    totalMusterrolls: number;
    totalPersonsWithFaceAuth: number;
    ekycDone: number;
    ekycNotDone: number;
}

export interface AttendanceData {
    date: string;
    attendance: number;
    faceAuthAttendance: number;
}

export interface AttendanceRecord {
    id: string;
    granularity: 'State' | 'District' | 'Block' | 'GP' | 'Village';
    location: string;
    state: string;
    district: string;
    block?: string;
    gp?: string;
    village?: string;
    kpiName: string;
    kpiValue: number | string;
    kpiType: 'count' | 'percentage';
    date: string;
}

// Mock dashboard metrics
export const mockDashboardMetrics: DashboardMetrics = {
    totalStates: 28,
    totalDistricts: 756,
    totalBlocks: 6850,
    totalGPs: 250000,
    totalVillages: 644000,
    totalMusterrolls: 1250000,
    totalPersonsWithFaceAuth: 45000000,
    ekycDone: 35000000,
    ekycNotDone: 10000000,
};

// Mock attendance data for charts
export const mockAttendanceData: AttendanceData[] = [
    { date: '2024-01', attendance: 2500000, faceAuthAttendance: 1800000 },
    { date: '2024-02', attendance: 2700000, faceAuthAttendance: 2100000 },
    { date: '2024-03', attendance: 2900000, faceAuthAttendance: 2400000 },
    { date: '2024-04', attendance: 3100000, faceAuthAttendance: 2700000 },
    { date: '2024-05', attendance: 3300000, faceAuthAttendance: 3000000 },
    { date: '2024-06', attendance: 3500000, faceAuthAttendance: 3200000 },
    { date: '2024-07', attendance: 3200000, faceAuthAttendance: 2900000 },
    { date: '2024-08', attendance: 3400000, faceAuthAttendance: 3100000 },
    { date: '2024-09', attendance: 3600000, faceAuthAttendance: 3400000 },
];

// Mock attendance records data
export const mockAttendanceRecords: AttendanceRecord[] = [
    // Uttar Pradesh only
    { id: '1', granularity: 'State', location: 'Uttar Pradesh', state: 'Uttar Pradesh', district: '', kpiName: 'Total Face Auth Attendance', kpiValue: 5500000, kpiType: 'count', date: '2024-09-13' },
    { id: '2', granularity: 'District', location: 'Allahabad', state: 'Uttar Pradesh', district: 'Allahabad', kpiName: 'Total Musterrolls with Face Auth', kpiValue: 45000, kpiType: 'count', date: '2024-09-13' },
    { id: '3', granularity: 'Block', location: 'Phulpur', state: 'Uttar Pradesh', district: 'Allahabad', block: 'Phulpur', kpiName: 'Total Villages with Face Auth', kpiValue: 150, kpiType: 'count', date: '2024-09-13' },
    { id: '4', granularity: 'GP', location: 'Rampur Gram Panchayat', state: 'Uttar Pradesh', district: 'Allahabad', block: 'Phulpur', gp: 'Rampur', kpiName: 'Active Workers with Face Auth', kpiValue: 2500, kpiType: 'count', date: '2024-09-13' },
    { id: '5', granularity: 'Village', location: 'Rampur Village', state: 'Uttar Pradesh', district: 'Allahabad', block: 'Phulpur', gp: 'Rampur', village: 'Rampur Village', kpiName: 'Face Auth Accuracy', kpiValue: '94%', kpiType: 'percentage', date: '2024-09-13' },
    // Add another district hierarchy for UP
    { id: '6', granularity: 'District', location: 'Kanpur', state: 'Uttar Pradesh', district: 'Kanpur', kpiName: 'Total Musterrolls with Face Auth', kpiValue: 32000, kpiType: 'count', date: '2024-09-13' },
    { id: '7', granularity: 'Block', location: 'Sadar', state: 'Uttar Pradesh', district: 'Kanpur', block: 'Sadar', kpiName: 'Total Villages with Face Auth', kpiValue: 120, kpiType: 'count', date: '2024-09-13' },
    { id: '8', granularity: 'GP', location: 'Chakeri GP', state: 'Uttar Pradesh', district: 'Kanpur', block: 'Sadar', gp: 'Chakeri', kpiName: 'Active Workers with Face Auth', kpiValue: 1800, kpiType: 'count', date: '2024-09-13' },
    { id: '9', granularity: 'Village', location: 'Chakeri Village', state: 'Uttar Pradesh', district: 'Kanpur', block: 'Sadar', gp: 'Chakeri', village: 'Chakeri Village', kpiName: 'Face Auth Accuracy', kpiValue: '92%', kpiType: 'percentage', date: '2024-09-13' },
];

// Mock API functions
export const mockAPI = {
    getDashboardMetrics: async (): Promise<DashboardMetrics> => {
        // Simulate API delay
        await new Promise((resolve) => setTimeout(resolve, 500));
        return mockDashboardMetrics;
    },

    getAttendanceData: async (): Promise<AttendanceData[]> => {
        await new Promise((resolve) => setTimeout(resolve, 300));
        return mockAttendanceData;
    },

    getAttendanceRecords: async (filters?: { state?: string; district?: string; block?: string; dateRange?: { start: string; end: string }; kpiType?: string; search?: string }): Promise<AttendanceRecord[]> => {
        await new Promise((resolve) => setTimeout(resolve, 400));

        let filteredRecords = [...mockAttendanceRecords];

        if (filters) {
            if (filters.state) {
                filteredRecords = filteredRecords.filter((record) => record.state.toLowerCase().includes(filters.state!.toLowerCase()));
            }

            if (filters.district) {
                filteredRecords = filteredRecords.filter((record) => record.district.toLowerCase().includes(filters.district!.toLowerCase()));
            }

            if (filters.search) {
                filteredRecords = filteredRecords.filter((record) => record.location.toLowerCase().includes(filters.search!.toLowerCase()) || record.kpiName.toLowerCase().includes(filters.search!.toLowerCase()));
            }

            if (filters.kpiType) {
                filteredRecords = filteredRecords.filter((record) => record.kpiType === filters.kpiType);
            }
        }

        return filteredRecords;
    },
};

// States list for filters
export const statesList = ['Uttar Pradesh'];

// KPI types for filters
export const kpiTypes = ['Total Face Auth Attendance', 'Face Auth Coverage', 'Total Musterrolls with Face Auth', 'eKYC Completion Rate', 'Total Villages with Face Auth', 'Active Workers with Face Auth', 'Face Auth Accuracy', 'Face Auth Devices Deployed'];
